import React, { useState, useEffect } from "react";
import {
  Card,
  Col,
  Typography,
  Tag,
  Space,
  Button,
  Popconfirm,
  Spin,
} from "antd";
import {
  DeleteOutlined,
  LinkOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  PoweroffOutlined,
} from "@ant-design/icons";
import { statusConfig } from "../Constants";

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

function renderModuleName(module) {
  const name = module?.name || "";
  const id = (module?.id || "").toLowerCase();
  const hasTM = /\u2122/.test(name) || name.includes("™");

  const stripTM = (s) =>
    s
      .replace(/\u2122/g, "")
      .replace(/™/g, "")
      .trim();
  const up = stripTM(name).toUpperCase();

  if (id === "fahras") {
    const a = up.slice(0, 3); // FAH
    const b = up.slice(3) + (hasTM ? "™" : "");
    return (
      <span className="module-title-text">
        <span className="module-title-suffix">{a}</span>
        <span className="module-title-prefix">{b}</span>
      </span>
    );
  }

  if (id === "jeyeda") {
    const p1 = up.slice(0, 1);
    const p2 = up.slice(1, 4);
    const p3 = up.slice(4) + (hasTM ? "™" : "");
    return (
      <span className="module-title-text">
        <span className="module-title-suffix">{p1}</span>
        <span className="module-title-prefix">{p2}</span>
        <span className="module-title-suffix">{p3}</span>
      </span>
    );
  }

  if (id === "dghelper") {
    const a = up.slice(0, 2); // DG
    const b = up.slice(2) + (hasTM ? "™" : "");
    return (
      <span className="module-title-text">
        <span className="module-title-suffix">{a}</span>
        <span className="module-title-prefix">{b}</span>
      </span>
    );
  }

  const prefix = "Skeye";
  if (
    name.toLowerCase().startsWith(prefix.toLowerCase()) &&
    name.length > prefix.length
  ) {
    const suffix = name.slice(prefix.length);
    return (
      <span className="module-title-text">
        <span className="module-title-prefix">{prefix}</span>
        <span className="module-title-suffix">{suffix}</span>
      </span>
    );
  }

  return <span className="module-title-text module-title-suffix">{name}</span>;
}

export default function ModuleCard({
  module,
  currentStatus,
  isActionLoading,
  isInitialLoading,
  onCreate,
  onToggle,
  onDelete,
  isBackendDown,
  isRoleUser,
}) {
  const isMobile = useIsMobile();
  const handleAction = async (actionFn) => {
    setLocalPending(true);
    try {
      await actionFn(module.id);
    } finally {
      setLocalPending(false);
    }
  };

  const [localPending, setLocalPending] = useState(false);

  useEffect(() => {
    if (!isActionLoading) setLocalPending(false);
  }, [isActionLoading]);

  useEffect(() => {
    if (currentStatus) {
      setLocalPending(false);
    }
  }, [currentStatus]);

  const effectiveStatus = isBackendDown ? "unknown" : currentStatus;
  const normalizedStatus = (effectiveStatus || "not-created")
    .toString()
    .toLowerCase();
  const statusMeta = statusConfig?.[normalizedStatus] ?? {
    color: "default",
    text: normalizedStatus,
  };

  const canCreate =
    normalizedStatus === "not-created" || normalizedStatus === "deleted";
  const isRunning = normalizedStatus === "running";

  // Combine all scenarios where interactions should be blocked
  const isActionLoadingCombined = isActionLoading || localPending;
  const isDisabled =
    isInitialLoading || isActionLoadingCombined || isBackendDown;
  const isLoading =
    (isInitialLoading || isActionLoadingCombined) && !isBackendDown;

  return (
    <Col xs={24} sm={12} lg={8}>
      <Card className="module-card" variant="borderless" hoverable>
        <div className="module-logo-wrap">
          <img
            src={module.logo}
            alt={module.name}
            className={`module-logo ${module.logoClass || ""}`.trim()}
          />
        </div>

        <Typography.Title level={5} className="module-title">
          {renderModuleName(module)}
        </Typography.Title>

        <div className="module-status-line">
          <Typography.Text>Container Status:</Typography.Text>
          {isLoading ? (
            <Spin size="small" />
          ) : (
            <Tag color={statusMeta.color}>{statusMeta.text}</Tag>
          )}
        </div>

        <Space size={8} wrap className="module-actions">
          <Button
            icon={<LinkOutlined />}
            aria-label="Open module"
            onClick={() =>
              window.open(module.link, "_blank", "noopener,noreferrer")
            }
            disabled={!isRunning || isDisabled}
          >
            {!isMobile && "Open"}
          </Button>

          {!isRoleUser && (
            <>
              <Button
                icon={<PlusOutlined />}
                aria-label="Create module"
                onClick={() => handleAction(onCreate)}
                disabled={!canCreate || isDisabled}
              >
                {!isMobile && "Create"}
              </Button>
              <Popconfirm
                title={`${isRunning ? "Stop" : "Start"} Module`}
                description={`Are you sure you want to ${isRunning ? "stop" : "start"} ${module.name}?`}
                onConfirm={() => {
                  handleAction(onToggle);
                }}
                okText="Yes"
                cancelText="No"
                disabled={canCreate || isDisabled}
              >
                <Button
                  type="primary"
                  aria-label={isRunning ? "Stop module" : "Start module"}
                  icon={
                    isRunning ? <PoweroffOutlined /> : <PlayCircleOutlined />
                  }
                  disabled={canCreate || isDisabled}
                >
                  {!isMobile && (isRunning ? "Stop" : "Start")}
                </Button>
              </Popconfirm>

              <Popconfirm
                title="Delete Module"
                description={`Are you sure you want to delete ${module.name}? This action cannot be undone.`}
                onConfirm={() => {
                  handleAction(onDelete);
                }}
                okText="Yes, Delete"
                cancelText="No"
                okButtonProps={{ danger: true }}
                disabled={canCreate || isDisabled}
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  aria-label="Delete module"
                  disabled={canCreate || isDisabled}
                >
                  {!isMobile && "Delete"}
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      </Card>
    </Col>
  );
}
